import { Group, Select, Text, Tooltip } from "@mantine/core";
import { IconLogout, IconSend } from "@tabler/icons-react";
import { Badge, Button, spacing } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { ReviewRunSummary } from "@models/review";
import { SaveStatus, type SaveState } from "./SaveStatus";
import { PUBLISH_BLOCKER_KEY, type PublishBlocker } from "./publishBlocker";
import { useToolbarSlots } from "./toolbarSlotsContext";
import "./ReviewToolbar.scss";

interface ReviewToolbarProps {
  /** Whether the current user may publish (`generation.admin`). */
  canPublish: boolean;
  /** Runs awaiting review, for the run select. */
  runs: ReviewRunSummary[];
  /** Per-run pending counts, keyed by runId (for the select option label). */
  pendingByRun: Record<string, number>;
  /** The active run id (the select's value). */
  activeRunId: string | null;
  /** Called when the reviewer picks a different run. */
  onSelectRun: (runId: string) => void;
  /** The decision save status. */
  saveState: SaveState;
  /** Why Publish is locked for the active run, or null when it is allowed. */
  publishBlocker: PublishBlocker | null;
  /** Opens the Publish confirm dialog. */
  onPublish: () => void;
}

/**
 * The single toolbar row that sits directly below the shell's global header —
 * there is no second app header or preview strip. It mirrors the old
 * reviewer-app top bar:
 *
 *   left  = a role pill (and, in the standalone preview only, the signed-in
 *           fake user's name);
 *   right = the run-select of runs awaiting review (with pending counts), the
 *           save status, Publish, and — in the preview only — a quiet
 *           "Preview - fake API" pill and a Log out button.
 *
 * Publish is shown ONLY to `generation.admin`; reviewers never see it. For an
 * admin it is disabled with a tooltip while anything is pending. The
 * preview-only parts come from `useToolbarSlots()`, whose provider lives under
 * `dev/` behind `__PREVIEW__` — inside the shell the context is `null` and the
 * toolbar is just the role pill, the run select and Publish.
 */
export function ReviewToolbar({
  canPublish,
  runs,
  pendingByRun,
  activeRunId,
  onSelectRun,
  saveState,
  publishBlocker,
  onPublish,
}: ReviewToolbarProps) {
  const { t } = useTranslation();
  const preview = useToolbarSlots();

  const options = runs.map((run) => {
    const pending = pendingByRun[run.runId] ?? 0;
    return {
      value: run.runId,
      label: t("review.toolbar.runOption", {
        runId: run.runId,
        count: pending,
      }),
    };
  });

  const publishDisabled = publishBlocker !== null;
  const publishTooltip = publishBlocker
    ? t(PUBLISH_BLOCKER_KEY[publishBlocker])
    : t("review.publish.ready");

  return (
    <div className="review-toolbar">
      <Group gap={spacing.sm} wrap="nowrap">
        <Badge
          color={canPublish ? "dark" : "blueprint"}
          variant={canPublish ? "filled" : "light"}
        >
          {canPublish ? t("review.toolbar.roleAdmin") : t("review.toolbar.roleReviewer")}
        </Badge>
        {preview && (
          <Text size="sm" c="dimmed" className="review-toolbar__user">
            {preview.userName}
          </Text>
        )}
      </Group>

      <Group gap={spacing.md} wrap="nowrap">
        <Select
          className="review-toolbar__select"
          data={options}
          value={activeRunId}
          onChange={(value) => value && onSelectRun(value)}
          placeholder={t("review.toolbar.runPlaceholder")}
          aria-label={t("review.toolbar.runLabel")}
          allowDeselect={false}
          searchable
          size="xs"
          nothingFoundMessage={t("review.toolbar.noRuns")}
        />
        <SaveStatus state={saveState} />
        {/* Publish is admin-only; reviewers never see it. */}
        {canPublish && (
          <Tooltip label={publishTooltip} withArrow>
            {/* Span wrapper so the tooltip still shows on a disabled button. */}
            <span>
              <Button
                size="xs"
                leftSection={<IconSend size={14} />}
                disabled={publishDisabled}
                onClick={onPublish}
              >
                {t("review.publish.button")}
              </Button>
            </span>
          </Tooltip>
        )}
        {preview && (
          <>
            <Badge color="gray" variant="light">
              {preview.pillLabel}
            </Badge>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconLogout size={14} />}
              onClick={preview.onLogOut}
            >
              {t("review.toolbar.logOut")}
            </Button>
          </>
        )}
      </Group>
    </div>
  );
}
